<template>
    <div class="statistics-section">
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ $t('statistics.pointsStanding') }}
            </h2>
        </div>
        <div class="flex flex-col md:flex-row gap-2 mb-3">
            <div class="w-full md:w-auto">
                <Select :options="periodOptions" v-model="selectedPeriod" option-value="value" optionLabel="label" />
            </div>
        </div>
        <Card class="mb-4 border-gray-200! dark:border-gray-700! border-1">
            <template #content>
                <Chart type="bar" :data="chartData" :options="chartOptions" class="h-80" />
            </template>
        </Card>

        <!-- User summary cards -->
        <div v-if="userSummary.length > 0" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <Card v-for="(user, index) in userSummary" :key="user.username"
                class="hover:shadow-md transition-shadow duration-200  border-gray-200! dark:border-gray-700! border-1">
                <template #content>
                    <div class="flex items-center space-x-3">
                        <div class="w-4 h-4 rounded-full flex-shrink-0" :style="{ backgroundColor: user.color }"></div>
                        <div class="flex-grow">
                            <p class="font-semibold">{{ user.username }}</p>
                            <p class="text-sm text-surface-600 dark:text-surface-400">{{ user.xp }} XP</p>
                        </div>
                        <div class="text-right">
                            <Badge v-if="index === 0" :value="'🥇 ' + $t('rank.first')" severity="warn" />
                            <Badge v-else-if="index === 1" :value="'🥈 ' + $t('rank.second')" severity="secondary" />
                            <Badge v-else-if="index === 2" :value="'🥉 ' + $t('rank.third')" severity="info" />
                            <span v-else class="text-sm text-surface-500 dark:text-surface-400">#{{ index + 1 }}</span>
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>

<script setup lang="ts">
import { getApiV1PerformedChores, type PerformedChoreDto } from "@/client";
import { useSignalR } from "@/composables/signalr";
import { useUserColors } from "@/composables/user-colors";
import type { EntityCreatedEvent } from "@/types/signalr-events";
import { useToast } from "primevue/usetoast";
import { computed, onMounted, onUnmounted, ref, watch, type Ref } from "vue";
import { useI18n } from "vue-i18n";
const { subscribe, unsubscribe } = useSignalR();
const { getChartColors, getUserColorByUsername } = useUserColors();
const toast = useToast();
const { t } = useI18n();



type Period = 'day' | 'week' | 'month';

const selectedPeriod = ref<Period>('month');
const periodOptions = ref([
    { label: computed(() => t('statistics.period.today')), value: 'day' },
    { label: computed(() => t('statistics.period.week')), value: 'week' },
    { label: computed(() => t('statistics.period.month')), value: 'month' },
]);

const periodTitle = computed(() => {
    switch (selectedPeriod.value) {
        case 'day': return t('statistics.period.today');
        case 'week': return t('statistics.period.week');
        case 'month': return t('statistics.period.month');
    }
    return '';
});

const fromDate = computed(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    switch (selectedPeriod.value) {
        case 'day':
            break;
        case 'week':
            date.setDate(date.getDate() - date.getDay());
            break;
        case 'month':
            date.setDate(1);
            break;
    }
    return date;
});
const performedChores: Ref<PerformedChoreDto[]> = ref([]);
watch(selectedPeriod, async (newPeriod) => {
    console.log(`Selected period changed to: ${newPeriod}. Loading data from ${fromDate.value.toISOString()}`);
    const response = await getApiV1PerformedChores({ query: { fromDate: fromDate.value } });
    if (response.error) {
        console.error("Error fetching performed chores:", response.error);
        toast.add({ severity: 'error', summary: t('common.error'), detail: t('common.errorLoadingData'), life: 3000 });
    }
    performedChores.value = response.data!;
}, { immediate: true });

const chartData = computed(() => {
    // Aggregate XP by user
    const userXpMap = new Map<string, number>();

    performedChores.value.forEach(chore => {
        chore.participants?.forEach(participant => {
            if (participant.username) {
                const currentXp = userXpMap.get(participant.username) || 0;
                const xpToAdd = participant.totalXpAwarded || 0;
                userXpMap.set(participant.username, currentXp + xpToAdd);
            }
        });
    });

    // Convert to arrays for chart
    const usernames = Array.from(userXpMap.keys());
    const xpValues = Array.from(userXpMap.values());
    const colors = getChartColors(usernames);

    return {
        labels: usernames,
        datasets: [
            {
                label: t('statistics.pointsWithPeriod', { period: periodTitle.value }),
                data: xpValues,
                backgroundColor: colors,
                borderColor: colors.map(color => color + '80'), // Add transparency to borders
                borderWidth: 1,
                borderRadius: 4,
                borderSkipped: false,
            },
        ],
    };
});

const chartOptions = computed(() => {
    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false, // Hide legend since we have labeled bars
            },
            title: {
                display: true,
                text: t('statistics.chartTitle', { period: periodTitle.value }),
                font: {
                    size: 18,
                    weight: 'bold'
                },
                padding: 20,
            },
            tooltip: {
                callbacks: {
                    label: function (context: any) {
                        return `${context.label}: ${context.parsed.y} XP`;
                    }
                },
                borderWidth: 1
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: t('statistics.pointsLabel'),
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                },
                ticks: {
                    font: {
                        size: 12
                    }
                },
                grid: {
                    lineWidth: 1
                }
            },
            x: {
                title: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 14,
                        weight: 'bold'
                    },
                    maxRotation: 0
                },
                grid: {
                    display: false
                }
            }
        },
        animation: {
            duration: 800,
            easing: 'easeInOutQuart'
        },
        layout: {
            padding: {
                top: 40,
                bottom: 10,
                left: 10,
                right: 10
            }
        }
    };
});

const userSummary = computed(() => {
    // Aggregate XP by user
    const userXpMap = new Map<string, number>();

    performedChores.value.forEach(chore => {
        chore.participants?.forEach(participant => {
            if (participant.username) {
                const currentXp = userXpMap.get(participant.username) || 0;
                const xpToAdd = participant.totalXpAwarded || 0;
                userXpMap.set(participant.username, currentXp + xpToAdd);
            }
        });
    });

    // Convert to array and sort by XP (descending)
    const usernames = Array.from(userXpMap.keys());

    const summary = usernames.map((username) => ({
        username,
        xp: userXpMap.get(username) || 0,
        color: getUserColorByUsername(username)
    }));

    return summary.sort((a, b) => b.xp - a.xp);
});

onMounted(() => {
    console.log("StatisticsSection mounted. Initial data load.");


    subscribe<EntityCreatedEvent<PerformedChoreDto>>(
        "PerformedChoreCreated",
        async (event) => {
            console.debug("PerformedChoreCreated event received:", event);
            performedChores.value.push(event.entity);
            // Limit the size of performedChores to avoid excessive memory use
            if (performedChores.value.length > 1000) {
                performedChores.value.pop();
            }
        }
    );
});

onUnmounted(() => {
    unsubscribe("PerformedChoreCreated");
});

</script>